import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import {useCallback} from 'react'

import type {LabelsQueryData} from './types'
import {buildRemoveLabelFromCurrentViewOperation, LABELS_CONNECTION_PATH} from './utils/label-connection-operations'
import {deleteLabelRequest} from './utils/label-requests'

type UseDeleteLabelOptions = {
  labelId: string
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setIsDialogOpen: (v: boolean) => void
  setErrorsPresent: (v: boolean) => void
}

type DeleteLabelHandler = () => void

export function useDeleteLabel({
  labelId,
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setIsDialogOpen,
  setErrorsPresent,
}: UseDeleteLabelOptions): DeleteLabelHandler {
  const queryClient = useQueryClient()

  const {mutate} = useConnectionMutation<void, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    mutationFn: () => deleteLabelRequest(repositoryNameWithOwner, labelId),
    onError: () => {
      setErrorsPresent(true)
    },
    onSuccess: () => {
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildRemoveLabelFromCurrentViewOperation(labelId)) : base,
        )
      }
    },
  })

  return useCallback(() => {
    setIsDialogOpen(false)
    mutate({operation: buildRemoveLabelFromCurrentViewOperation(labelId)})
  }, [mutate, labelId, setIsDialogOpen])
}
